// =============================================================================
// LP Express — Advertorial template renderer.
//
// Renders a `generatedContent.advertorial` blob (see
// shared/lp-express-advertorial.ts) as a long-form editorial article, ported
// from the template library's longform-editorial advertorial: ad-disclosure
// bar → breadcrumb → headline + dek → author byline (AI avatar / date /
// verified badge) → article body sections (heading, lead, paragraphs, inline
// AI editorial photos, numbered steps, bullets, inline pill CTAs) → testimonial
// block (REAL reviews via the shared review picker) → offer block with derived
// pricing + callout → reader comments → legal disclaimer — with a sticky
// product-recommendation rail on desktop and a sticky bottom CTA bar on mobile.
//
//  • Copy is fully editable in the builder via the path-based `setText` ops the
//    other templates use (paths like ["advertorial","sections",0,"heading"]).
//  • AI imagery comes from `generatedContent.advertorialImages` per-slot
//    records (pending/ready/failed) with an in-builder retry on failed slots.
//    The rail's product image is NEVER AI-generated — it defaults to the real
//    product photo and is swappable only via the media picker/upload.
//  • Prices, ratings, review counts and discounts are ALWAYS derived from the
//    real product/offer/sale/review data — the generated copy never carries
//    dollar amounts, and strikethrough/discount framing only appears when a
//    real active discount exists on the run.
//  • Brand adaptation comes from the shared --color-brand-* CSS variables the
//    renderer already sets (brandCssVarsV2), so the same layout re-skins per
//    brand with readable contrast.
// =============================================================================
import { useEffect, useRef, useState } from "react";
import {
  BadgeCheck, CheckCircle2, Loader2, RotateCw, Sparkles, Star,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  useLpEditor,
  LpImageActions,
  LpReviewActions,
  useLpMediaOverride,
  useLpReviewOverride,
  type LpReviewChoice,
} from "@/components/lp-editor";
import type { Product } from "@shared/schema";
import type { LpReceipt, LpReviewDisplay } from "@/lib/lp-express-pricing";
import {
  normalizeLpAdvertorialContent,
  type LpAdvertorialContent,
  type LpAdvertorialSection,
} from "@shared/lp-express-advertorial";

export type AdvertorialCtx = {
  brand: any;
  offer: { checkoutUrl?: string } | any;
  generatedContent: any;
  logoUrl: string | null;
  bundleProductPhotos: string[];
  bundleImage?: string | null;
  products?: Product[];
  receipt?: LpReceipt;
  reviewDisplay?: LpReviewDisplay;
  typography?: { heading?: string; body?: string } | any;
  /** In-builder retry for a failed AI image slot; absent on the public page. */
  onRetrySlot?: (mergeKey: "advertorialImages", slotKey: string) => void;
};

// Shared brand palette (same CSS vars the quiz/listicle/long-form templates
// use, set by the renderer root via brandCssVarsV2 → adapts per brand).
const HEADING = "var(--color-brand-navy)";
const ACCENT = "var(--color-brand-green)";
const DEEP = "var(--color-brand-deepnavy)";
const CREAM = "var(--color-brand-cream)";
const TEXT = "var(--color-brand-text)";
const MUTED = "var(--color-brand-muted)";
const CARD = "var(--color-brand-card)";
const BORDER = "var(--color-brand-lightborder)";
const GOLD = "var(--color-brand-gold)";
const CORAL = "var(--color-brand-coral)";
const PAGE_BG = "#FFFFFF";

// Media-override key for the sticky rail's product photo (real photo only —
// no AI regen on this slot; swap via the picker/upload).
const RAIL_PRODUCT_SLOT = "advertorial:rail-product";

// ── Image slot helpers ───────────────────────────────────────────────────────

type SlotRecord = { key: string; imageUrl?: string | null; imageStatus?: string; altText?: string };

function useAdvertorialSlots(gc: any): Record<string, SlotRecord> {
  const arr: SlotRecord[] = Array.isArray(gc?.advertorialImages) ? gc.advertorialImages : [];
  const map: Record<string, SlotRecord> = {};
  for (const s of arr) if (s?.key) map[s.key] = s;
  return map;
}

// Media-override key for an AI slot (used by BOTH the slot's rendering and
// its LpImageActions controls — must stay identical or an upload won't show).
function advertorialMediaKey(slotKey: string): string {
  return `advertorial:img:${slotKey}`;
}

// One AI image slot: ready → the image; pending → spinner; failed → subdued
// placeholder with an in-builder retry (public page shows nothing clickable).
// In the builder, EVERY state also gets the standard hover controls
// (Upload / Regenerate / "Use AI" revert) — a READY image must be updatable
// too, not just a failed one. A user upload (media override) always wins over
// the AI image, including on the public page.
function ImgSlot({
  slot,
  slotKey,
  className,
  style,
  onRetry,
  testId,
  withActions = true,
}: {
  slot: SlotRecord | undefined;
  slotKey: string;
  className?: string;
  style?: React.CSSProperties;
  onRetry?: (key: string) => void;
  testId: string;
  /** False for slots too small to host the hover controls (byline avatar);
   *  the caller mounts LpImageActions on a larger wrapper instead. */
  withActions?: boolean;
}) {
  const status = slot?.imageStatus;
  const overrideUrl = useLpMediaOverride(advertorialMediaKey(slotKey));
  const aiUrl = slot?.imageUrl && status !== "failed" ? slot.imageUrl : null;
  const shownUrl = overrideUrl ?? aiUrl;
  const actions = withActions ? (
    <LpImageActions
      slotKey={advertorialMediaKey(slotKey)}
      canRegenerate={!!onRetry && status !== "pending"}
      onRegenerate={onRetry ? () => onRetry(slotKey) : undefined}
      testIdPrefix={testId}
    />
  ) : null;
  if (shownUrl) {
    return (
      <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
        <img
          src={shownUrl}
          alt={slot?.altText || ""}
          loading="lazy"
          className={className}
          style={{ objectFit: "cover", display: "block", width: "100%", height: "100%", ...style }}
          data-testid={testId}
        />
        {actions}
      </div>
    );
  }
  const failed = status === "failed";
  return (
    <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
      <div
        className={className}
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 6, width: "100%", height: "100%", minHeight: 90, backgroundColor: "#F1EDE4", ...style,
        }}
        data-testid={testId}
      >
        {failed ? (
          onRetry ? (
            <button
              type="button"
              onClick={() => onRetry(slotKey)}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold"
              style={{ color: HEADING, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}
              data-testid={`button-retry-${testId}`}
            >
              <RotateCw className="h-3 w-3" /> Retry image
            </button>
          ) : (
            <Sparkles className="h-4 w-4" style={{ color: MUTED }} />
          )
        ) : (
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: MUTED }} />
        )}
      </div>
      {actions}
    </div>
  );
}

// ── Small shared pieces ──────────────────────────────────────────────────────

// Inline article CTA pill (mid-article + offer block). Editor / no-URL →
// non-navigating span so copy stays editable.
function CtaButton({
  label,
  path,
  href,
  testId,
  block = false,
}: {
  label: string;
  path: (string | number)[];
  href?: string;
  testId: string;
  /** Full-width block button (offer/rail); default is an inline pill. */
  block?: boolean;
}) {
  const editor = useLpEditor();
  const inner = (
    <EditableText
      value={label}
      path={path}
      as="span"
      noHideDelete
      placeholder="Check availability"
      testId={`${testId}-label`}
    />
  );
  const style: React.CSSProperties = block
    ? {
        display: "block", width: "100%", padding: "15px 0", backgroundColor: ACCENT, color: "#FFFFFF",
        fontSize: 16, fontWeight: 800, textAlign: "center", borderRadius: 12, letterSpacing: "0.02em",
        textDecoration: "none", boxShadow: "0 4px 14px rgba(0,0,0,0.16)", cursor: "pointer",
      }
    : {
        display: "inline-block", padding: "12px 28px", backgroundColor: ACCENT, color: "#FFFFFF",
        fontSize: 14, fontWeight: 800, textAlign: "center", borderRadius: 9999,
        textDecoration: "none", boxShadow: "0 3px 10px rgba(0,0,0,0.15)", cursor: "pointer",
      };
  if (editor || !href) {
    return (
      <span style={{ ...style, ...(editor ? {} : { opacity: 0.7, cursor: "not-allowed" }) }} data-testid={testId} data-lp-cta>
        {inner}
      </span>
    );
  }
  return (
    <a href={href} style={style} data-testid={testId} data-lp-cta>
      {inner}
    </a>
  );
}

// Commenter initials, derived from the name ("Sarah M." → "SM").
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "A";
  return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join("");
}

// ── Testimonial card ─────────────────────────────────────────────────────────
// Real stored reviews AUTO-FILL the cards whenever the brand has them:
// precedence is picked override (generatedContent.reviewOverrides[slotKey],
// via the shared LpReviewActions picker) > the Nth real review > the
// GENERATED blob item (reviewsBlock.items[i], fully editable via the standard
// setText paths). Generated copy is only a fallback so brands with zero
// stored reviews still get a full editable testimonial block.
function TestimonialSlot({
  slot,
  item,
  options,
}: {
  slot: number;
  item?: { name: string; text: string; rating: number };
  options: LpReviewChoice[];
}) {
  const editor = useLpEditor();
  const slotKey = `advertorial:reviews:${slot}`;
  const override = useLpReviewOverride(slotKey);
  // Picked override wins, then the Nth real stored review; the generated
  // blob item only renders when the brand has no real review for this slot.
  const rev: LpReviewChoice | null = override ?? options[slot] ?? null;
  const AVATARS = ["#E8927C", "#7BBFB2", "#A78BCA", "#E0A45E", "#8CB4D9", "#C4A265"];
  if (!rev && !item && !editor) return null;
  const name = rev ? rev.reviewer || "Verified customer" : item?.name ?? "";
  const rating = rev ? rev.rating : item?.rating ?? 5;
  const base: (string | number)[] = ["advertorial", "reviewsBlock", "items", slot];
  return (
    <div className="group/lpreview relative" style={{ backgroundColor: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, padding: "14px 16px" }} data-testid={`advertorial-review-${slot}`}>
      {options.length > 0 && (
        <LpReviewActions slotKey={slotKey} reviews={options} testIdPrefix={`advertorial-review-${slot}`} />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: AVATARS[slot % AVATARS.length], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#fff" }}>{(name || "A")[0]}</span>
        </div>
        {rev ? (
          <span style={{ fontSize: 13, fontWeight: 700, color: HEADING }}>{name}</span>
        ) : (
          <EditableText
            value={item?.name ?? ""}
            path={[...base, "name"]}
            as="span"
            style={{ fontSize: 13, fontWeight: 700, color: HEADING }}
            placeholder="Customer name"
            testId={`text-advertorial-review-name-${slot}`}
          />
        )}
        <span style={{ display: "inline-flex", color: "#F5A623" }}>
          {Array.from({ length: Math.max(1, Math.min(5, rating)) }).map((_, s) => (
            <Star key={s} style={{ width: 11, height: 11 }} className="fill-current" />
          ))}
        </span>
        <span style={{ fontSize: 8, fontWeight: 700, color: ACCENT, backgroundColor: "rgba(45,122,79,0.08)", padding: "2px 5px", borderRadius: 3, marginLeft: "auto" }}>Verified</span>
      </div>
      {rev ? (
        <p style={{ fontSize: 13, lineHeight: 1.6, color: TEXT, margin: 0 }}>"{rev.reviewText}"</p>
      ) : (
        <EditableText
          value={item?.text ?? ""}
          path={[...base, "text"]}
          as="p"
          multiline
          style={{ fontSize: 13, lineHeight: 1.6, color: TEXT, margin: 0 }}
          placeholder="Testimonial quote"
          testId={`text-advertorial-review-text-${slot}`}
        />
      )}
    </div>
  );
}

// ── Reader comment card ──────────────────────────────────────────────────────
// Like the testimonial cards, each reader comment can be swapped for a REAL
// stored review via the shared picker (the pick is snapshotted on
// generatedContent.reviewOverrides under `advertorial:comments:<slot>`).
// Unlike testimonials, comments NEVER auto-fill from real reviews — the
// generated casual copy stays until the operator explicitly picks one
// (the testimonial slots already consume the first real reviews, so
// auto-filling here would render the same quotes twice on the page).
function CommentSlot({
  slot,
  item,
  options,
}: {
  slot: number;
  item: { name: string; text: string; meta: string };
  options: LpReviewChoice[];
}) {
  const slotKey = `advertorial:comments:${slot}`;
  const override = useLpReviewOverride(slotKey);
  const name = override ? override.reviewer || "Verified customer" : item.name;
  return (
    <div className="group/lpreview relative" style={{ display: "flex", gap: 10 }} data-testid={`advertorial-comment-${slot}`}>
      {options.length > 0 && (
        <LpReviewActions slotKey={slotKey} reviews={options} testIdPrefix={`advertorial-comment-${slot}`} />
      )}
      <div style={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: CREAM, border: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: HEADING }}>{initialsOf(name)}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ backgroundColor: "#F4F2ED", borderRadius: 10, padding: "10px 12px" }}>
          {override ? (
            <p style={{ fontSize: 12, fontWeight: 700, color: HEADING, margin: "0 0 3px" }} data-testid={`text-advertorial-comment-name-${slot}`}>{name}</p>
          ) : (
            <EditableText
              value={item.name}
              path={["advertorial", "comments", "items", slot, "name"]}
              as="p"
              style={{ fontSize: 12, fontWeight: 700, color: HEADING, margin: "0 0 3px" }}
              placeholder="Commenter name"
              testId={`text-advertorial-comment-name-${slot}`}
            />
          )}
          {override ? (
            <p style={{ fontSize: 13, lineHeight: 1.55, color: TEXT, margin: 0 }} data-testid={`text-advertorial-comment-text-${slot}`}>{override.reviewText}</p>
          ) : (
            <EditableText
              value={item.text}
              path={["advertorial", "comments", "items", slot, "text"]}
              as="p"
              multiline
              style={{ fontSize: 13, lineHeight: 1.55, color: TEXT, margin: 0 }}
              placeholder="Comment text"
              testId={`text-advertorial-comment-text-${slot}`}
            />
          )}
        </div>
        <EditableText
          value={item.meta}
          path={["advertorial", "comments", "items", slot, "meta"]}
          as="p"
          style={{ fontSize: 10, color: MUTED, margin: "4px 0 0 4px" }}
          placeholder="Like · Reply · 2d"
          testId={`text-advertorial-comment-meta-${slot}`}
        />
      </div>
    </div>
  );
}

// ── Article body section ─────────────────────────────────────────────────────

function ArticleSection({
  section,
  index,
  slots,
  retry,
  checkoutUrl,
  headingFont,
}: {
  section: LpAdvertorialSection;
  index: number;
  slots: Record<string, SlotRecord>;
  retry?: (key: string) => void;
  checkoutUrl?: string;
  headingFont: string;
}) {
  const editor = useLpEditor();
  const base: (string | number)[] = ["advertorial", "sections", index];
  // Per-section AI copy regen: the SAME single advertorial workflow re-runs
  // with feedback auto-scoped to this section, and the server merges ONLY this
  // section back (see regenerate-advertorial-section). The control anchors on
  // the heading; every copy field carries the block key so it dims together.
  const regenKey = `advertorial-section-${index}`;
  return (
    <EditableElement elementKey={`advertorial-section-${index}`} toolbarSide="left">
      <section style={{ marginBottom: 36, ...(editor && section.hidden ? { opacity: 0.45 } : {}) }} data-testid={`advertorial-section-${index}`}>
        {(section.heading || editor) && (
          <EditableText
            value={section.heading}
            path={[...base, "heading"]}
            as="h2"
            className={headingFont}
            style={{ fontSize: 24, fontWeight: 800, color: HEADING, lineHeight: 1.3, margin: "0 0 14px", letterSpacing: "-0.01em" }}
            placeholder={`Section ${index + 1} heading`}
            testId={`text-advertorial-section-heading-${index}`}
            regenBlockKey={regenKey}
            regenAnchor
          />
        )}
        {/* Optional lead: rendered ONLY when the AI wrote one (then it's
            editable). Empty leads show nothing — even in the editor — at the
            user's request; an empty "click to add" slot read as clutter. */}
        {section.lead && (
          <EditableText
            value={section.lead}
            path={[...base, "lead"]}
            as="p"
            multiline
            style={{ fontSize: 17, lineHeight: 1.65, color: TEXT, fontWeight: 500, margin: "0 0 14px" }}
            placeholder="Lead paragraph"
            testId={`text-advertorial-section-lead-${index}`}
            regenBlockKey={regenKey}
          />
        )}
        {section.imageSlot && (
          <figure style={{ margin: "0 0 16px" }}>
            <div style={{ borderRadius: 12, overflow: "hidden", minHeight: 200 }}>
              <ImgSlot
                slot={slots[section.imageSlot]}
                slotKey={section.imageSlot}
                onRetry={retry}
                style={{ minHeight: 200 }}
                testId={`img-advertorial-${section.imageSlot}`}
              />
            </div>
          </figure>
        )}
        {section.paragraphs.map((p, pi) => (
          <EditableText
            key={pi}
            value={p}
            path={[...base, "paragraphs", pi]}
            as="p"
            multiline
            style={{ fontSize: 15, lineHeight: 1.75, color: TEXT, margin: "0 0 14px" }}
            placeholder="Paragraph"
            testId={`text-advertorial-section-${index}-para-${pi}`}
            regenBlockKey={regenKey}
          />
        ))}
        {section.steps.length > 0 && (
          <div style={{ backgroundColor: CREAM, borderRadius: 12, border: `1px solid ${BORDER}`, padding: "16px 18px", margin: "0 0 14px" }} data-testid={`advertorial-steps-${index}`}>
            {section.steps.map((s, si) => (
              <div key={si} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: si < section.steps.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: HEADING, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>{si + 1}</span>
                </div>
                <EditableText
                  value={s}
                  path={[...base, "steps", si]}
                  as="span"
                  multiline
                  style={{ fontSize: 14, lineHeight: 1.6, color: TEXT }}
                  placeholder="Step"
                  testId={`text-advertorial-section-${index}-step-${si}`}
                  regenBlockKey={regenKey}
                />
              </div>
            ))}
          </div>
        )}
        {section.bullets.length > 0 && (
          <div style={{ margin: "0 0 14px" }} data-testid={`advertorial-bullets-${index}`}>
            {section.bullets.map((b, bi) => (
              <div key={bi} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                <CheckCircle2 style={{ width: 15, height: 15, color: ACCENT, flexShrink: 0, marginTop: 4 }} />
                <EditableText
                  value={b}
                  path={[...base, "bullets", bi]}
                  as="span"
                  multiline
                  style={{ fontSize: 14, lineHeight: 1.65, color: TEXT }}
                  placeholder="Bullet point"
                  testId={`text-advertorial-section-${index}-bullet-${bi}`}
                  regenBlockKey={regenKey}
                />
              </div>
            ))}
          </div>
        )}
        {section.ctaText && (
          <div style={{ textAlign: "center", margin: "20px 0 6px" }}>
            <CtaButton
              label={section.ctaText}
              path={[...base, "ctaText"]}
              href={checkoutUrl}
              testId={`button-advertorial-section-cta-${index}`}
            />
          </div>
        )}
      </section>
    </EditableElement>
  );
}

// ── Sticky desktop product rail ──────────────────────────────────────────────

function ProductRail({ advertorial, ctx }: { advertorial: LpAdvertorialContent; ctx: AdvertorialCtx }) {
  const editor = useLpEditor();
  const receipt = ctx.receipt;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  // Rail product image: real product photo ONLY (media picker / upload swap —
  // never AI regen on this slot).
  const overrideUrl = useLpMediaOverride(RAIL_PRODUCT_SLOT);
  const defaultUrl = ctx.bundleImage || ctx.bundleProductPhotos[0] || null;
  const imgUrl = overrideUrl ?? defaultUrl;
  return (
    <aside className="hidden lg:block" style={{ width: 300, flexShrink: 0 }} data-testid="advertorial-rail">
      <div style={{ position: "sticky", top: 24 }}>
        <div style={{ backgroundColor: CARD, borderRadius: 14, border: `1.5px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
          <div style={{ backgroundColor: HEADING, padding: "10px 14px", textAlign: "center" }}>
            <EditableText
              value={advertorial.rail.recLabel}
              path={["advertorial", "rail", "recLabel"]}
              as="p"
              className="uppercase"
              style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: GOLD, margin: 0, textAlign: "center" }}
              placeholder="Editor's pick"
              testId="text-advertorial-rail-label"
            />
          </div>
          {/* Product shot uses `contain` (not `cover`) so the whole product is
              visible — a packaged tub/bottle gets its top & bottom cropped by
              cover. Light padded background makes the fit look intentional. */}
          <div className="group/lpimg relative" style={{ backgroundColor: "#FFFFFF" }}>
            {imgUrl ? (
              <img src={imgUrl} alt="" style={{ width: "100%", height: 220, objectFit: "contain", display: "block", padding: 14 }} data-testid="img-advertorial-rail-product" />
            ) : (
              <div style={{ height: 220, backgroundColor: "#F1EDE4" }} data-testid="img-advertorial-rail-product" />
            )}
            <LpImageActions
              slotKey={RAIL_PRODUCT_SLOT}
              canRegenerate={false}
              productPhotos={ctx.bundleProductPhotos}
              revertLabel="Use default"
              revertTitle="Revert to the default product photo"
              testIdPrefix="advertorial-rail-product"
            />
          </div>
          <div style={{ padding: 16 }}>
            {ctx.reviewDisplay?.hasReviews && (
              <p style={{ fontSize: 11, fontWeight: 600, color: MUTED, margin: "0 0 8px" }} data-testid="text-advertorial-rail-rating">
                <span style={{ color: "#F5A623" }}>★★★★★</span> {ctx.reviewDisplay.ratingText} · {ctx.reviewDisplay.reviewCountText} reviews
              </p>
            )}
            {advertorial.rail.benefits.map((b, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 7, marginBottom: 6 }}>
                <CheckCircle2 style={{ width: 13, height: 13, color: ACCENT, flexShrink: 0, marginTop: 2 }} />
                <EditableText
                  value={b}
                  path={["advertorial", "rail", "benefits", i]}
                  as="span"
                  style={{ fontSize: 12, lineHeight: 1.5, color: TEXT }}
                  placeholder="Benefit"
                  testId={`text-advertorial-rail-benefit-${i}`}
                />
              </div>
            ))}
            {receipt && (
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "10px 0 12px" }}>
                <span style={{ fontSize: 22, fontWeight: 900, color: HEADING }} data-testid="text-advertorial-rail-price">{receipt.priceTodayLabel}</span>
                {receipt.hasDiscount && receipt.retailLabel && (
                  <span style={{ fontSize: 13, color: "#999", textDecoration: "line-through" }}>{receipt.retailLabel}</span>
                )}
                {receipt.hasDiscount && receipt.discountPercent != null && (
                  <span style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 800, fontSize: 9, padding: "2px 6px", borderRadius: 4, letterSpacing: "0.08em" }}>
                    {receipt.discountPercent}% OFF
                  </span>
                )}
              </div>
            )}
            <CtaButton
              label={advertorial.rail.ctaText}
              path={["advertorial", "rail", "ctaText"]}
              href={checkoutUrl}
              testId="button-advertorial-rail-cta"
              block
            />
          </div>
        </div>
      </div>
    </aside>
  );
}

// ── Sticky bottom CTA bar (mobile) ──────────────────────────────────────────

function AdvertorialStickyBar({
  advertorial,
  ctx,
  heroRef,
}: {
  advertorial: LpAdvertorialContent;
  ctx: AdvertorialCtx;
  heroRef: React.RefObject<HTMLElement | null>;
}) {
  const editor = useLpEditor();
  const [visible, setVisible] = useState(false);
  const receipt = ctx.receipt;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;

  useEffect(() => {
    if (editor) return;
    const el = heroRef.current;
    if (!el) return;
    // ownerDocument-scoped observer so this works inside the mobile-preview
    // iframe too (same rule as the other LP primitives).
    const win = el.ownerDocument?.defaultView ?? window;
    const observer = new win.IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { root: null, threshold: 0, rootMargin: "-20% 0px 0px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [editor, heroRef]);

  const bar = (
    <div
      className="lg:hidden"
      style={{ backgroundColor: DEEP, borderTop: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 -6px 24px rgba(0,0,0,0.25)" }}
      data-testid="advertorial-sticky-bar"
    >
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          {receipt?.hasDiscount && receipt.discountPercent != null && (
            <span className="uppercase" style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 900, fontSize: 10, letterSpacing: "0.12em", padding: "2px 7px", borderRadius: 4 }}>
              {receipt.discountPercent}% OFF
            </span>
          )}
          {receipt && <span style={{ color: "#fff", fontWeight: 900, fontSize: 15, lineHeight: 1 }}>{receipt.priceTodayLabel}</span>}
          {receipt?.hasDiscount && receipt.retailLabel && (
            <span style={{ color: "rgba(255,255,255,0.4)", textDecoration: "line-through", fontSize: 11 }}>{receipt.retailLabel}</span>
          )}
        </div>
        {editor || !checkoutUrl ? (
          <span style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "8px 20px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap" }} data-lp-cta>
            <EditableText value={advertorial.stickyCtaText} path={["advertorial", "stickyCtaText"]} as="span" noHideDelete placeholder="Check availability" testId="button-advertorial-sticky-cta-label" />
          </span>
        ) : (
          <a href={checkoutUrl} style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "8px 20px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap", textDecoration: "none" }} data-testid="button-advertorial-sticky-cta" data-lp-cta>
            {advertorial.stickyCtaText}
          </a>
        )}
      </div>
    </div>
  );

  // Editor: show the bar inline at the end of the stack so its copy is
  // editable without it covering the canvas.
  if (editor) return <div style={{ marginTop: 12 }}>{bar}</div>;
  if (!visible) return null;
  return <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50 }}>{bar}</div>;
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function LpExpressAdvertorial({ ctx }: { ctx: AdvertorialCtx }) {
  const editor = useLpEditor();
  const heroRef = useRef<HTMLElement | null>(null);
  const advertorial = normalizeLpAdvertorialContent(ctx.generatedContent?.advertorial);
  const slots = useAdvertorialSlots(ctx.generatedContent);
  const retry = ctx.onRetrySlot ? (key: string) => ctx.onRetrySlot!("advertorialImages", key) : undefined;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  const receipt = ctx.receipt;
  const headingFont = ctx.typography?.heading ?? "";
  const reviewOptions = (ctx.reviewDisplay?.allReviews ?? []) as LpReviewChoice[];
  const testimonialItems = Array.isArray(advertorial.reviewsBlock.items)
    ? advertorial.reviewsBlock.items
    : [];

  // Visible sections keep their ORIGINAL index for edit paths; hidden ones are
  // skipped on the public page and shown dimmed in the editor.
  const visibleSections = advertorial.sections
    .map((s, idx) => ({ s, idx }))
    .filter(({ s }) => (editor ? true : !s.hidden));

  return (
    <div style={{ minHeight: "100vh", backgroundColor: PAGE_BG }} className={ctx.typography?.body ?? ""} data-testid="advertorial-page">
      {/* Masthead: brand logo + breadcrumb. (The old top "ADVERTORIAL"
          ad-disclosure strip was removed at the user's request — the page now
          opens directly with the brand header. `adLabel` stays in the content
          model but is no longer rendered.) */}
      <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "12px 16px" }}>
        <div style={{ maxWidth: 1020, margin: "0 auto", display: "flex", alignItems: "center", gap: 14 }}>
          {ctx.logoUrl && (
            <img src={ctx.logoUrl} alt="" style={{ width: 34, height: 34, objectFit: "contain" }} data-testid="img-advertorial-logo" />
          )}
          <EditableText
            value={advertorial.breadcrumb}
            path={["advertorial", "breadcrumb"]}
            as="span"
            style={{ fontSize: 11, color: MUTED }}
            placeholder="Home › Wellness › Reviews"
            testId="text-advertorial-breadcrumb"
          />
        </div>
      </div>

      {/* Two-column article layout: article body + sticky desktop rail. */}
      <div style={{ maxWidth: 1020, margin: "0 auto", padding: "28px 16px 48px", display: "flex", gap: 40, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0, maxWidth: 680, margin: "0 auto" }}>
          {/* Headline + dek + byline */}
          <section ref={heroRef} style={{ marginBottom: 28 }}>
            <EditableText
              value={advertorial.headline}
              path={["advertorial", "headline"]}
              as="h1"
              className={headingFont}
              style={{ fontSize: 32, fontWeight: 900, color: HEADING, lineHeight: 1.2, margin: "0 0 12px", letterSpacing: "-0.015em" }}
              placeholder="Advertorial headline"
              testId="text-advertorial-headline"
            />
            <EditableText
              value={advertorial.dek}
              path={["advertorial", "dek"]}
              as="p"
              multiline
              style={{ fontSize: 17, lineHeight: 1.55, color: MUTED, margin: "0 0 18px" }}
              placeholder="Sub-headline / dek"
              testId="text-advertorial-dek"
            />
            {/* Byline: AI avatar + author + date + verified badge. The avatar
                circle (42px) is too small to host the hover image controls, so
                they mount on the byline ROW instead (hover the row → controls
                appear at its top-right, targeting the avatar slot). */}
            <div className="group/lpimg relative" style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 18, borderBottom: `1px solid ${BORDER}` }} data-testid="advertorial-byline">
              <div style={{ width: 42, height: 42, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
                <ImgSlot slot={slots["avatar"]} slotKey="avatar" onRetry={retry} style={{ minHeight: 42 }} testId="img-advertorial-avatar" withActions={false} />
              </div>
              <LpImageActions
                slotKey={advertorialMediaKey("avatar")}
                canRegenerate={!!retry && slots["avatar"]?.imageStatus !== "pending"}
                onRegenerate={retry ? () => retry("avatar") : undefined}
                testIdPrefix="advertorial-avatar"
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <EditableText
                    value={advertorial.byline.name}
                    path={["advertorial", "byline", "name"]}
                    as="span"
                    style={{ fontSize: 13, fontWeight: 700, color: HEADING }}
                    placeholder="Author name"
                    testId="text-advertorial-byline-name"
                  />
                  {advertorial.byline.verified && (
                    <BadgeCheck style={{ width: 14, height: 14, color: ACCENT }} data-testid="icon-advertorial-verified" />
                  )}
                </div>
                <EditableText
                  value={advertorial.byline.dateLine}
                  path={["advertorial", "byline", "dateLine"]}
                  as="p"
                  style={{ fontSize: 11, color: MUTED, margin: 0 }}
                  placeholder="Updated January 3, 2026"
                  testId="text-advertorial-byline-date"
                />
              </div>
            </div>
          </section>

          {/* Article body sections */}
          {visibleSections.map(({ s, idx }) => (
            <ArticleSection
              key={idx}
              section={s}
              index={idx}
              slots={slots}
              retry={retry}
              checkoutUrl={checkoutUrl}
              headingFont={headingFont}
            />
          ))}

          {/* Testimonials — real stored reviews auto-fill the cards when the
              brand has them (picker still lets you swap which one per card);
              generated blob items (editable) only fill slots with no real
              review, so zero-review brands still get the full block. */}
          {(testimonialItems.length > 0 || reviewOptions.length > 0) && (
            <EditableElement elementKey="advertorial-reviews" toolbarSide="left">
              <section style={{ marginBottom: 36, backgroundColor: CREAM, borderRadius: 14, padding: "22px 18px", border: `1px solid ${BORDER}` }} data-testid="advertorial-reviews">
                <EditableText
                  value={advertorial.reviewsBlock.heading}
                  path={["advertorial", "reviewsBlock", "heading"]}
                  as="h2"
                  className={headingFont}
                  style={{ fontSize: 22, fontWeight: 800, color: HEADING, lineHeight: 1.3, margin: "0 0 14px", textAlign: "center" }}
                  placeholder="What customers are saying"
                  testId="text-advertorial-reviews-heading"
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {Array.from({
                    length: Math.min(
                      3,
                      Math.max(testimonialItems.length, reviewOptions.length),
                    ),
                  }).map((_, i) => (
                    <TestimonialSlot
                      key={i}
                      slot={i}
                      item={testimonialItems[i]}
                      options={reviewOptions}
                    />
                  ))}
                </div>
                {(advertorial.reviewsBlock.footnote || editor) && (
                  <EditableText
                    value={advertorial.reviewsBlock.footnote}
                    path={["advertorial", "reviewsBlock", "footnote"]}
                    as="p"
                    style={{ fontSize: 10, color: MUTED, margin: "12px 0 0", textAlign: "center" }}
                    placeholder="Footnote (e.g. individual results may vary)"
                    testId="text-advertorial-reviews-footnote"
                  />
                )}
              </section>
            </EditableElement>
          )}

          {/* Offer block — pricing DERIVED from real data. */}
          <EditableElement elementKey="advertorial-offer" toolbarSide="left">
            <section style={{ marginBottom: 36 }} data-testid="advertorial-offer">
              <div style={{ backgroundColor: CARD, borderRadius: 14, border: `1.5px solid ${BORDER}`, padding: "22px 20px", boxShadow: "0 2px 14px rgba(0,0,0,0.05)" }}>
                <EditableText
                  value={advertorial.offer.heading}
                  path={["advertorial", "offer", "heading"]}
                  as="h2"
                  className={headingFont}
                  style={{ fontSize: 23, fontWeight: 800, color: HEADING, lineHeight: 1.3, margin: "0 0 12px" }}
                  placeholder="Where to get it"
                  testId="text-advertorial-offer-heading"
                />
                {advertorial.offer.paragraphs.map((p, pi) => (
                  <EditableText
                    key={pi}
                    value={p}
                    path={["advertorial", "offer", "paragraphs", pi]}
                    as="p"
                    multiline
                    style={{ fontSize: 14, lineHeight: 1.7, color: TEXT, margin: "0 0 12px" }}
                    placeholder="Offer paragraph"
                    testId={`text-advertorial-offer-para-${pi}`}
                  />
                ))}
                {receipt && (
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "4px 0 14px" }}>
                    <span style={{ fontSize: 26, fontWeight: 900, color: HEADING }} data-testid="text-advertorial-offer-price">{receipt.priceTodayLabel}</span>
                    {receipt.hasDiscount && receipt.retailLabel && (
                      <span style={{ fontSize: 15, color: "#999", textDecoration: "line-through" }}>{receipt.retailLabel}</span>
                    )}
                    {receipt.hasDiscount && receipt.discountPercent != null && (
                      <span style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 800, fontSize: 10, padding: "3px 8px", borderRadius: 4, letterSpacing: "0.08em" }} data-testid="badge-advertorial-save">
                        SAVE {receipt.discountPercent}%
                      </span>
                    )}
                  </div>
                )}
                <CtaButton
                  label={advertorial.offer.ctaText}
                  path={["advertorial", "offer", "ctaText"]}
                  href={checkoutUrl}
                  testId="button-advertorial-offer-cta"
                  block
                />
                {(advertorial.offer.calloutTitle || advertorial.offer.calloutBody || editor) && (
                  <div style={{ backgroundColor: "rgba(240, 200, 80, 0.12)", border: `1px solid ${GOLD}`, borderRadius: 10, padding: "12px 14px", marginTop: 16 }} data-testid="advertorial-offer-callout">
                    <EditableText
                      value={advertorial.offer.calloutTitle}
                      path={["advertorial", "offer", "calloutTitle"]}
                      as="p"
                      style={{ fontSize: 13, fontWeight: 800, color: HEADING, margin: "0 0 4px" }}
                      placeholder="Callout title"
                      testId="text-advertorial-callout-title"
                    />
                    <EditableText
                      value={advertorial.offer.calloutBody}
                      path={["advertorial", "offer", "calloutBody"]}
                      as="p"
                      multiline
                      style={{ fontSize: 12, lineHeight: 1.6, color: TEXT, margin: 0 }}
                      placeholder="Callout body"
                      testId="text-advertorial-callout-body"
                    />
                  </div>
                )}
              </div>
            </section>
          </EditableElement>

          {/* Reader comments — clearly-casual, fully editable copy. */}
          {(advertorial.comments.items.length > 0 || editor) && (
            <EditableElement elementKey="advertorial-comments" toolbarSide="left">
              <section style={{ marginBottom: 36 }} data-testid="advertorial-comments">
                <EditableText
                  value={advertorial.comments.heading}
                  path={["advertorial", "comments", "heading"]}
                  as="h2"
                  className={headingFont}
                  style={{ fontSize: 20, fontWeight: 800, color: HEADING, lineHeight: 1.3, margin: "0 0 14px", paddingBottom: 10, borderBottom: `1px solid ${BORDER}` }}
                  placeholder="What readers are saying"
                  testId="text-advertorial-comments-heading"
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {advertorial.comments.items.map((c, i) => (
                    <CommentSlot key={i} slot={i} item={c} options={reviewOptions} />
                  ))}
                </div>
              </section>
            </EditableElement>
          )}

          {/* Legal disclaimer */}
          {(advertorial.disclaimer || editor) && (
            <EditableElement elementKey="advertorial-disclaimer" toolbarSide="left">
              <section style={{ marginBottom: 28, paddingTop: 16, borderTop: `1px solid ${BORDER}` }} data-testid="advertorial-disclaimer">
                <EditableText
                  value={advertorial.disclaimer}
                  path={["advertorial", "disclaimer"]}
                  as="p"
                  multiline
                  style={{ fontSize: 10, lineHeight: 1.6, color: MUTED, margin: 0 }}
                  placeholder="Legal disclaimer"
                  testId="text-advertorial-disclaimer-text"
                />
              </section>
            </EditableElement>
          )}

        </div>

        <ProductRail advertorial={advertorial} ctx={ctx} />
      </div>

      <AdvertorialStickyBar advertorial={advertorial} ctx={ctx} heroRef={heroRef} />
    </div>
  );
}
